/**
 * Saved ArcGIS Server / GeoServer connections — Settings › ArcGIS Servers /
 * GeoServer (`SettingsPage` renders this once per `type`).
 *
 * A connection saved here is what the Builder's "Add layers › Saved
 * connection" picker lists — see AddLayersDialog. This screen never shows or
 * asks for a connection's password again after it is first saved: editing
 * one leaves the stored password untouched unless a new one is typed, and
 * the status badge is drawn from `lastVerifiedAt`/`lastError`, which the
 * server sets on every save and on "Test connection" — never by a probe run
 * from here.
 *
 * Any signed-in user reaches this tab now (see SettingsPage), because any
 * signed-in user can build an app that adds layers from a saved connection —
 * but only an admin manages the connections themselves. Add/Edit/Delete/Test
 * are hidden below for a non-admin; the list stays visible either way, since
 * picking a connection to use requires seeing what exists. This mirrors the
 * server: those four actions are still admin-only there (server_connections_router.py).
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, HelpCircle, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import type { ServerConnection } from '../../types';
import { usePermissions } from '../../auth/AuthContext';
import { serverConnectionsService, type ServerConnectionInput } from '../../services/serverConnectionsService';

interface FormState {
  name: string;
  baseUrl: string;
  username: string;
  password: string;
}

const EMPTY_FORM: FormState = { name: '', baseUrl: '', username: '', password: '' };

const COPY: Record<
  'arcgis' | 'geoserver',
  { noun: string; article: 'a' | 'an'; urlLabel: string; urlPlaceholder: string; urlHint: string; settingsPath: string }
> = {
  arcgis: {
    noun: 'ArcGIS Server',
    article: 'an',
    urlLabel: 'REST services URL',
    urlPlaceholder: 'https://host/arcgis/rest/services',
    // The services root, so the Builder can browse and pick from every
    // MapServer/FeatureServer on the server (see AddLayersDialog's "Saved
    // connection" picker) rather than one connection per service. A single
    // service's own MapServer/FeatureServer URL also works, for a
    // connection scoped to just that one.
    urlHint: 'The REST services root — not the Manager or Portal web app. One specific MapServer/FeatureServer URL also works if this connection should only ever offer that one service.',
    settingsPath: 'Settings › ArcGIS Servers',
  },
  geoserver: {
    noun: 'GeoServer',
    article: 'a',
    urlLabel: 'GeoServer URL',
    urlPlaceholder: 'https://host/geoserver',
    urlHint: 'The GeoServer root — every layer it publishes can be browsed from one connection.',
    settingsPath: 'Settings › GeoServer',
  },
};

function StatusBadge({ connection }: { connection: ServerConnection }) {
  if (connection.lastError) {
    return (
      <span
        title={connection.lastError}
        className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[10.5px] font-semibold text-red-600"
      >
        <AlertTriangle className="h-3 w-3" /> Connection failed
      </span>
    );
  }
  if (connection.lastVerifiedAt) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-[10.5px] font-semibold text-teal-700">
        <CheckCircle2 className="h-3 w-3" /> Connected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-semibold text-slate-500">
      <HelpCircle className="h-3 w-3" /> Not tested
    </span>
  );
}

export default function ServerConnectionsTab({ type }: { type: 'arcgis' | 'geoserver' }) {
  const { isAdmin } = usePermissions();
  const copy = COPY[type];
  const [connections, setConnections] = useState<ServerConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setConnections(await serverConnectionsService.list(type));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load saved connections.');
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    void load();
    // Switching between the ArcGIS Servers and GeoServer sections reuses
    // this same component with a different `type` — reload rather than show
    // the other section's stale list for a moment.
    setShowForm(false);
  }, [load]);

  const validate = (isEditing: boolean): Partial<Record<keyof FormState, string>> => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = 'Name is required';
    if (!form.baseUrl.trim()) next.baseUrl = 'URL is required';
    else if (!/^https?:\/\//i.test(form.baseUrl.trim())) next.baseUrl = 'Enter a full http(s) URL';
    if (!form.username.trim()) next.username = 'Username is required';
    if (!isEditing && !form.password.trim()) next.password = 'Password is required';
    return next;
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setSubmitError(null);
    setShowForm(true);
  };

  const openEdit = (connection: ServerConnection) => {
    setEditingId(connection.id);
    setForm({ name: connection.name, baseUrl: connection.baseUrl, username: connection.username, password: '' });
    setErrors({});
    setSubmitError(null);
    setShowForm(true);
  };

  const submit = async () => {
    const nextErrors = validate(editingId !== null);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: ServerConnectionInput = {
        name: form.name.trim(),
        baseUrl: form.baseUrl.trim(),
        username: form.username.trim(),
        ...(form.password.trim() ? { password: form.password.trim() } : {}),
      };
      if (editingId) await serverConnectionsService.update(editingId, payload);
      else await serverConnectionsService.create(type, payload);
      setShowForm(false);
      await load();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Could not save this connection.');
    } finally {
      setSubmitting(false);
    }
  };

  const test = async (connection: ServerConnection) => {
    setTestingId(connection.id);
    try {
      const updated = await serverConnectionsService.test(connection.id);
      setConnections((current) => current.map((c) => (c.id === updated.id ? updated : c)));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not test this connection.');
    } finally {
      setTestingId(null);
    }
  };

  const remove = async (connection: ServerConnection) => {
    if (!window.confirm(`Delete "${connection.name}"? Any application already using a layer added from it keeps working — this only removes it from the picker.`)) {
      return;
    }
    try {
      await serverConnectionsService.remove(connection.id);
      await load();
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not delete this connection.');
    }
  };

  if (loading) {
    return <p className="py-10 text-center text-sm text-slate-400">Loading connections…</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-[11.5px] text-slate-500">
        Connect {copy.article} {copy.noun} once, here, and every application's Builder can add layers from
        it without typing the URL or credentials again — see "Add layers › Saved connection".
      </p>

      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          {connections.length} connection{connections.length === 1 ? '' : 's'}
        </p>
        {isAdmin && (
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0F2942] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#16395C]"
          >
            <Plus className="h-3.5 w-3.5" />
            Connect {copy.article} {copy.noun}
          </button>
        )}
      </div>

      {!isAdmin && (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
          Only an administrator can add, edit, delete, or test connections. You can use any connection
          below when adding layers in the Builder.
        </p>
      )}

      {loadError && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
          {loadError}
        </p>
      )}

      {showForm && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-[#0F2942]">
              {editingId ? `Edit ${copy.noun} connection` : `Connect ${copy.article} ${copy.noun}`}
            </h4>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              aria-label="Close form"
              className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block sm:col-span-2">
              <span className="text-xs font-semibold text-slate-600">Name *</span>
              <input
                value={form.name}
                onChange={(event) => setForm((c) => ({ ...c, name: event.target.value }))}
                placeholder={`e.g. Head office ${copy.noun}`}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-400"
              />
              {errors.name && <span className="mt-1 block text-[11px] text-red-600">{errors.name}</span>}
            </label>

            <label className="block sm:col-span-2">
              <span className="text-xs font-semibold text-slate-600">{copy.urlLabel} *</span>
              <input
                value={form.baseUrl}
                onChange={(event) => setForm((c) => ({ ...c, baseUrl: event.target.value }))}
                placeholder={copy.urlPlaceholder}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-400"
              />
              <span className="mt-1 block text-[10.5px] text-slate-400">{copy.urlHint}</span>
              {errors.baseUrl && <span className="mt-1 block text-[11px] text-red-600">{errors.baseUrl}</span>}
            </label>

            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Username *</span>
              <input
                value={form.username}
                onChange={(event) => setForm((c) => ({ ...c, username: event.target.value }))}
                autoComplete="off"
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-400"
              />
              {errors.username && <span className="mt-1 block text-[11px] text-red-600">{errors.username}</span>}
            </label>

            <label className="block">
              <span className="text-xs font-semibold text-slate-600">
                Password {editingId ? '(leave blank to keep the current one)' : '*'}
              </span>
              <input
                type="password"
                value={form.password}
                onChange={(event) => setForm((c) => ({ ...c, password: event.target.value }))}
                autoComplete="new-password"
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-400"
              />
              {errors.password && <span className="mt-1 block text-[11px] text-red-600">{errors.password}</span>}
            </label>
          </div>

          <p className="mt-3 text-[10.5px] leading-relaxed text-slate-400">
            The password is encrypted before it is stored. When a layer from this connection is added to an
            application, its URL carries the credentials so the map can request data from it directly —
            that URL then lives inside that application's own saved configuration.
          </p>

          {submitError && (
            <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
              {submitError}
            </p>
          )}

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={submitting}
              className="rounded-lg bg-[#2DD4BF] px-4 py-2 text-xs font-semibold text-[#0B1F38] transition hover:bg-[#5EEAD4] disabled:opacity-50"
            >
              {submitting ? 'Saving…' : editingId ? 'Save changes' : 'Connect'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {connections.length === 0 ? (
        <p className="gl-panel border-dashed py-10 text-center text-sm text-slate-400">
          No {copy.noun} connections yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {connections.map((connection) => (
            <li
              key={connection.id}
              className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 truncate text-[13px] font-medium text-slate-800">
                  {connection.name}
                  <StatusBadge connection={connection} />
                </p>
                <p className="mt-0.5 truncate text-[11px] text-slate-400">
                  {connection.baseUrl} · {connection.username}
                </p>
              </div>
              {isAdmin && (
                <div className="flex flex-shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => void test(connection)}
                    disabled={testingId === connection.id}
                    className="rounded px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                  >
                    {testingId === connection.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      'Test connection'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(connection)}
                    aria-label={`Edit ${connection.name}`}
                    className="rounded p-1.5 text-slate-500 hover:bg-slate-100"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void remove(connection)}
                    aria-label={`Delete ${connection.name}`}
                    className="rounded p-1.5 text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
